import React, { useCallback, useRef } from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";

import type { JSX, ReactNode } from "react";

interface IWrapperPlayer {
  children: ReactNode;
  isResponsive?: boolean;
  url?: string | null;
  isLoading?: boolean;
}

function WrapperPlayer({
  children,
  url,
  isResponsive,
  isLoading,
}: IWrapperPlayer): JSX.Element {
  const { t } = useTranslation("common");

  const playerClassNames = classNames(
    "bg-black size-full flex items-center justify-center",
    {
      "absolute left-0 top-0": isResponsive,
    },
  );

  return (
    <div className={playerClassNames}>
      {!url && !isLoading && (
        <span className="text-overlay-foreground">{t("noFeed")}</span>
      )}
      {children}
    </div>
  );
}

interface ISimplePlayer {
  src?: string | null;
  showControls?: boolean;
  isResponsive?: boolean;
  isLoading?: boolean;
  isPlaying?: boolean;
  isMuted?: boolean;
}

function SimplePlayer({
  src,
  isResponsive,
  isLoading,
  showControls = true,
  isPlaying = false,
  isMuted = true,
}: ISimplePlayer): JSX.Element {
  const playerRef = useRef<
    (HTMLVideoElement & { api?: { destroy?: () => void } }) | null
  >(null);

  const containerClassNames = classNames({
    "relative pt-[56.25%]": isResponsive,
    "size-full": !isResponsive,
  });

  const handleReady = (): void => {
    if (isPlaying) {
      playerRef.current?.play();
    }
  };

  const setPlayerRef = useCallback((player: HTMLVideoElement) => {
    if (!player) return;
    playerRef.current = player;
  }, []);

  return (
    <div className={containerClassNames}>
      <WrapperPlayer
        isLoading={isLoading}
        isResponsive={isResponsive}
        url={src}
      >
        {src ? (
          <ReactPlayer
            ref={setPlayerRef}
            playsInline
            autoPlay={false}
            controls={showControls}
            fallback={null}
            height="100%"
            muted={isMuted}
            onReady={handleReady}
            src={src}
            width="100%"
          />
        ) : null}
      </WrapperPlayer>
    </div>
  );
}

export default SimplePlayer;
